/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState, useEffect } from 'react';
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Switch,
  Toolbar,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardActions } from 'modules/dashboard/slice';
import {
  selectPmsSync,
  selectVisualFilters,
  selectHotelName,
  selectBreakdownDetails,
  selectEnableHotelGroups,
  selectResetBookingDate,
  selectTabList,
} from 'modules/dashboard/selectors';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { getSelectedBreakdown, shortcut } from 'modules/dashboard/functions';
import { FILTER_VALUES, SHORTCUT_KEY, TAGS } from 'modules/dashboard/constants';
import PAGINATION from 'modules/common/constants/pagination-limits';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { CheckboxDropdown, CustomStack } from 'modules/common/components';
import ActionButton from 'modules/common/components/action-button';
import DataUpdatedLabel from 'modules/dashboard/components/tab-container/data-updated-label';
import PmsToggle from 'modules/dashboard/components/tab-container/inner-filter/components/pms-toggle';
import BookingWindow from 'modules/dashboard/components/tab-container/inner-filter/components/booking-window';
import {
  getFocusOnFilter,
  getSubBreakdownFilter,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import {
  selectRegionMapFocusOn,
  selectRegionMapChartBreakdown,
  selectRegionalMapOtb,
  selectIsRegionDetailed,
  selectIsFocusOnSet,
} from 'modules/dashboard/components/tab-container/regional-map-tab/selectors';
import { regionalMapActions } from 'modules/dashboard/components/tab-container/regional-map-tab/slice';
import useRegionalMapCommandPipeline from '../../hooks/use-regional-map-command-pipeline';
import { COMMON_FOCUS_ON_OPTIONS } from '../../../trend-tabs/constants';
import { isEmpty } from '../../../../../../common/helpers/object';
/**
 * Filters for Regional Map widget include PMS Sync, Booking Dates, Chart Breakdown and Focus on
 * @param {Number} tabValue - Pace widget tab value
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {Boolean} hotelTimezone - Timezone of selected hotel
 * @param {Number} toggleDisabled - PMS sync toggle disabled state
 * @param {Boolean} isLoaded - Visual is loaded state
 * @param {String} reportType - Report type of active tab
 * @returns
 */
const RegionalMapFilter = ({
  tabValue,
  latestDate,
  hotelTimezone,
  toggleDisabled,
  isLoaded,
  reportType,
}) => {
  const dispatch = useDispatch();

  // Selected filter values are obtained from redux store. Refer each selector function for details.
  const pmsSyncToggle = useSelector(selectPmsSync);
  const visualFilters = useSelector(selectVisualFilters);
  const focusOn = useSelector(selectRegionMapFocusOn);
  const chartBreakdown = useSelector(selectRegionMapChartBreakdown);
  const hotel = useSelector(selectHotelName);
  const breakdownData = useSelector(selectBreakdownDetails);
  const organizationId = useSelector(selectOrganizationId);
  const customBooking = useSelector(selectRegionalMapOtb);
  const isDetailed = useSelector(selectIsRegionDetailed);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const isFocusOnSet = useSelector(selectIsFocusOnSet);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [chartBreakdownList, setChartBreakdownList] = useState([]);
  const [focusOnList, setFocusOnList] = useState([]);
  // breakdown that the current focus on list was built for
  const [focusOnListBreakdown, setFocusOnListBreakdown] = useState();
  const [selectedBreakdown, setSelectedBreakdown] = useState();
  const [isDetailDisabled, setIsDetailDisabled] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [previousFocusOnSelection, setPreviousFocusOnSelection] = useState([]);
  //
  useEffect(() => {
    if (focusOn?.length > 0 && !isFocusOnSet) {
      dispatch(regionalMapActions.setIsFocusOnSet(true));
    }
  }, [focusOn, isFocusOnSet]);
  // check whether all focus on options are selected or not
  useEffect(() => {
    if (focusOnList?.length > 0) {
      const allFocusOnOptionsSelected = focusOnList.length - 1 === focusOn.length;
      setAllSelected(allFocusOnOptionsSelected);
    }
  }, [focusOn, focusOnList]);

  // To select otb or custom booking window
  const handleOTBChange = (event) => {
    const { value } = event.target;
    dispatch(regionalMapActions.setRegionalMapOtb(value !== 'false'));
    dispatch(regionalMapActions.setRegionMapBookingDate(resetBookingDate));
  };

  // Reset button function to set filter panel values to default
  const clearSearch = () => {
    dispatch(regionalMapActions.setRegionalMapOtb(false));
    dispatch(regionalMapActions.setRegionMapBookingDate(resetBookingDate));
    dispatch(dashboardActions.setPmsSync(false));
    const allOptions = focusOnList.filter(
      (option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
    );
    dispatch(regionalMapActions.setRegionMapFocusOn(allOptions));
    setAllSelected(true);
    dispatch(regionalMapActions.setIsRegionDetailed(false));
    dispatch(
      regionalMapActions.setRegionMapChartBreakdown(
        visualFilters?.filter(
          (filter) => filter?.tags?.includes(TAGS.SEGMENT_BREAKDOWN) && filter?.isDefault === true
        )[0].id
      )
    );
    setPreviousFocusOnSelection(
      chartBreakdownList.reduce((acc, breakdown) => {
        acc[breakdown.id] = { detailedOn: [], detailedOff: [] };
        return acc;
      }, {})
    );
  };

  // Triggered when visualFilter value changes
  useEffect(() => {
    // Set chart breakdown list
    const subChartBreakdownList = getSubBreakdownFilter(visualFilters, TAGS.REGION);
    if (subChartBreakdownList) {
      setChartBreakdownList(subChartBreakdownList);
      setPreviousFocusOnSelection(
        subChartBreakdownList.reduce((acc, breakdown) => {
          acc[breakdown.id] = { detailedOn: [], detailedOff: [] };
          return acc;
        }, {})
      );
    }
  }, [visualFilters]);

  // Triggered on value change of chartBreakdown, visualFilters, breakdownData or selectedBreakdown
  useEffect(() => {
    // Set focus on list relevant for selected chart breakdown
    if (chartBreakdown) {
      let breakdown;
      if (
        selectedBreakdown === FILTER_VALUES.RATE_CATEGORY ||
        selectedBreakdown === FILTER_VALUES.RATE_CATEGORY_RAW
      ) {
        breakdown = FILTER_VALUES.RATE_CATEGORY_RAW;
      } else {
        breakdown = selectedBreakdown;
      }
      const focusOnFilter = getFocusOnFilter(breakdownData, breakdown);
      setFocusOnList(focusOnFilter);
      setFocusOnListBreakdown(selectedBreakdown);
      if (isEmpty(previousFocusOnSelection)) {
        dispatch(
          regionalMapActions.setRegionMapFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      }
      if (
        isDetailed &&
        !isEmpty(previousFocusOnSelection) &&
        isEmpty(previousFocusOnSelection[chartBreakdown].detailedOn)
      ) {
        dispatch(
          regionalMapActions.setRegionMapFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      } else if (
        !isDetailed &&
        !isEmpty(previousFocusOnSelection) &&
        isEmpty(previousFocusOnSelection[chartBreakdown].detailedOff)
      ) {
        dispatch(
          regionalMapActions.setRegionMapFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      }
    }
  }, [chartBreakdown, visualFilters, breakdownData, selectedBreakdown]);

  // Triggered on hotel change
  useEffect(() => {
    // Get hotel breakdown sub categories data
    if (hotel?.id) {
      const query = `hotelId=${hotel?.id}&limit=${PAGINATION.BREAKDOWN_DATA}`;
      dispatch(dashboardActions.getBreakdown({ organizationId, query }));
    }
  }, [hotel]);
  //
  const focusOnLabel = focusOn?.length ? 'Focused on' : 'Select Focus on';

  // Shortcut function to set pms sync value
  shortcut(tabValue, TAB_VALUE.REGION_MAP, SHORTCUT_KEY.PMS_TOGGLE, () =>
    dispatch(dashboardActions.setPmsSync(!pmsSyncToggle))
  );

  // Triggered on isDetailed, chartBreakdown, visualFilters change
  useEffect(() => {
    // Assign values to selected breakdown on chart breakdown or isDetails toggle change
    const breakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    setSelectedBreakdown(breakdown);
  }, [isDetailed, chartBreakdown, visualFilters]);

  // Triggered on isDetailed, selectedBreakdown change
  useEffect(() => {
    // Disabled rate category details toggle
    if (
      selectedBreakdown === FILTER_VALUES.RATE_CATEGORY ||
      selectedBreakdown === FILTER_VALUES.RATE_CATEGORY_RAW
    ) {
      dispatch(regionalMapActions.setIsRegionDetailed(false));
      const focusOnFilter = getFocusOnFilter(breakdownData, FILTER_VALUES.RATE_CATEGORY_RAW);
      setFocusOnList(focusOnFilter);
      setFocusOnListBreakdown(selectedBreakdown);
      if (isEmpty(previousFocusOnSelection)) {
        dispatch(
          regionalMapActions.setRegionMapFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      } else if (
        !isEmpty(previousFocusOnSelection) &&
        !isEmpty(previousFocusOnSelection[chartBreakdown].detailedOff)
      ) {
        dispatch(
          regionalMapActions.setRegionMapFocusOn(
            previousFocusOnSelection[chartBreakdown].detailedOff
          )
        );
      }
      setIsDetailDisabled(true);
    } else {
      setIsDetailDisabled(false);
    }
  }, [isDetailed, selectedBreakdown]);
  /**
   * set breakdown value
   * @param {*} event - onChanged event
   */
  const handleBreakdownChange = (event) => {
    if (previousFocusOnSelection[chartBreakdown]) {
      if (isDetailed) {
        previousFocusOnSelection[chartBreakdown].detailedOn = focusOn;
      } else {
        previousFocusOnSelection[chartBreakdown].detailedOff = focusOn;
      }
    }
    dispatch(regionalMapActions.setRegionMapChartBreakdown(event.target.value));
    dispatch(
      regionalMapActions.setRegionMapFocusOn(
        isDetailed
          ? previousFocusOnSelection[event.target.value]?.detailedOn
          : previousFocusOnSelection[event.target.value]?.detailedOff
      )
    );
  };
  /**
   * set isDetailed value
   * @param {*} event - onChanged event
   */
  const handleIsDetailedChange = (event) => {
    dispatch(regionalMapActions.setIsRegionDetailed(event.target.checked));
    if (previousFocusOnSelection[chartBreakdown]) {
      if (event.target.checked) {
        previousFocusOnSelection[chartBreakdown].detailedOff = focusOn;
      } else {
        previousFocusOnSelection[chartBreakdown].detailedOn = focusOn;
      }
    }
    if (
      event.target.checked &&
      !isEmpty(previousFocusOnSelection) &&
      previousFocusOnSelection[chartBreakdown] &&
      !isEmpty(previousFocusOnSelection[chartBreakdown].detailedOn)
    ) {
      dispatch(
        regionalMapActions.setRegionMapFocusOn(previousFocusOnSelection[chartBreakdown].detailedOn)
      );
    } else if (
      !event.target.checked &&
      !isEmpty(previousFocusOnSelection) &&
      !isEmpty(previousFocusOnSelection[chartBreakdown].detailedOff)
    ) {
      dispatch(
        regionalMapActions.setRegionMapFocusOn(previousFocusOnSelection[chartBreakdown].detailedOff)
      );
    }
  };
  /**
   * set focus on value
   * @param {*} event - on changed event
   * @param {*} value - selected value
   */
  const handleFocusOnChange = (event, value, reason) => {
    // dispatch(regionalMapActions.setRegionMapFocusOn(value));
    if (reason === 'selectOption' || reason === 'removeOption') {
      if (
        value.find((option) => option.label === COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label) &&
        value?.filter((option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label)
          .length === focusOn.length &&
        value?.length === focusOnList.length
      ) {
        dispatch(regionalMapActions.setRegionMapFocusOn([]));
        if (previousFocusOnSelection[chartBreakdown]) {
          if (isDetailed) {
            previousFocusOnSelection[chartBreakdown].detailedOn = [];
          } else {
            previousFocusOnSelection[chartBreakdown].detailedOff = [];
          }
        }
        setAllSelected(false);
      } else if (
        value.find((option) => option.label === COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label)
      ) {
        // Select all options if "Select All" is not selected
        const allOptions = focusOnList.filter(
          (option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
        );
        dispatch(regionalMapActions.setRegionMapFocusOn(allOptions));
        if (previousFocusOnSelection[chartBreakdown]) {
          if (isDetailed) {
            previousFocusOnSelection[chartBreakdown].detailedOn = allOptions;
          } else {
            previousFocusOnSelection[chartBreakdown].detailedOff = allOptions;
          }
        }
        setAllSelected(true);
      } else {
        dispatch(regionalMapActions.setRegionMapFocusOn(value));
        if (previousFocusOnSelection[chartBreakdown]) {
          if (isDetailed) {
            previousFocusOnSelection[chartBreakdown].detailedOn = value;
          } else {
            previousFocusOnSelection[chartBreakdown].detailedOff = value;
          }
        }
        setAllSelected(
          value?.filter((option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label)
            .length ===
            focusOnList?.filter(
              (option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            ).length
        );
      }
    }
  };
  // reset focus on option when hotel groups is selected
  useEffect(() => {
    dispatch(regionalMapActions.setRegionMapFocusOn([]));
    const subChartBreakdownList = getSubBreakdownFilter(visualFilters, TAGS.REGION);
    if (subChartBreakdownList) {
      setChartBreakdownList(subChartBreakdownList);
      setPreviousFocusOnSelection(
        subChartBreakdownList.reduce((acc, breakdown) => {
          acc[breakdown.id] = { detailedOn: [], detailedOff: [] };
          return acc;
        }, {})
      );
    }
  }, [hotel, enableHotelGroups]);
  //
  // Regional map command flow implementation
  // focus on list is rebuilt after a breakdown change, until then it holds previous breakdown options
  const currentBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
  const isFocusOnListReady = !currentBreakdown || focusOnListBreakdown === currentBreakdown;
  useRegionalMapCommandPipeline(tabValue, {
    toggleDisabled,
    isLoaded,
    customBooking,
    chartBreakdownList,
    isDetailDisabled,
    focusOnList,
    isFocusOnListReady,
  });
  //
  return (
    <>
      <PmsToggle
        disabled={toggleDisabled}
        value={pmsSyncToggle}
        onChange={(event) => dispatch(dashboardActions.setPmsSync(event.target.checked))}
      />
      <br />
      <BookingWindow
        vOTB={customBooking}
        customBooking={customBooking}
        handleOTBChange={handleOTBChange}
        reportType={reportType}
        latestDate={latestDate}
        otbValue={false}
      />
      <br />
      <CustomStack cssClassName={styles.innerFilter}>
        <FormControl className="segment-breakdown">
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ color: '#0009' }}>Breakdown</Typography>
            <FormControl>
              <FormControlLabel
                control={<Switch checked={isDetailed} onChange={handleIsDetailedChange} />}
                sx={{ '& .css-1cn7wjw-MuiTypography-root': { fontSize: 12 } }}
                label="Details"
                disabled={isDetailDisabled}
              />
            </FormControl>
          </Grid>
          <RadioGroup value={chartBreakdown ?? null} onChange={handleBreakdownChange}>
            {chartBreakdownList?.map((item) => (
              <FormControlLabel
                key={item?.id}
                value={item?.id}
                control={<Radio />}
                label={item?.label}
                sx={{
                  my: -1,
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <FormControl className="focuson">
          <CheckboxDropdown
            options={focusOnList}
            values={focusOn}
            label={focusOnLabel}
            onchange={handleFocusOnChange}
            isOptionEqualTo={(option, value) => option?.id === value?.id}
            allSelected={allSelected}
          />
        </FormControl>
      </CustomStack>
      <br />
      <CustomStack cssClassName={styles.innerFilter}>
        <ActionButton onClick={clearSearch} label="RESET" />
      </CustomStack>
      <Toolbar />
      <DataUpdatedLabel
        latestDate={latestDate}
        hotelTimezone={hotelTimezone}
        toggleDisabled={toggleDisabled}
      />
    </>
  );
};
//
export default RegionalMapFilter;
